import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import CloseIcon from '@mui/icons-material/Close';
import { 
    Box, Typography, Button, Modal, TextField, MenuItem, 
    IconButton, Grid, CircularProgress, Switch, FormControlLabel, Divider, Paper, Alert
} from '@mui/material';
import { useCurrency } from '../utils/currency';
import { isUaeProperty, getTourismDirhamRate, getTourismDirhamTierLabel } from '../utils/dubaiHospitalityTax';

const DEFAULT_TAX_STATE = { 
    name: '',
    rate: '', 
    rateType: 'percentage',
    cgst: '', 
    sgst: '', 
    serviceType: 'room', 
    applicableTo: 'Room', 
    minThreshold: '', 
    maxThreshold: '',
    allow_itc: false, 
    is_exempt: false, 
    status: 'Active',
    startDate: '',
    endDate: ''
};

export default function TaxFormModal({ open, onClose, onSave, editingTax = null, loading = false, hotel = null }) {
    const { currency, currencySymbol } = useCurrency();
    const { tenants } = useSelector((state) => state.auth);
    const authData = JSON.parse(localStorage.getItem('adminAuth') || '{}');
    const tenantId = authData.tenantId;
    const activeTenant = (tenants || []).find(t => 
        String(t.tenantId || t.id || t._id) === String(tenantId) ||
        String(t.id || t._id) === String(authData.hotelId)
    );
    const effectiveHotel = hotel || activeTenant || authData?.hotel || authData?.tenant || authData?.property || {};
    const isUae = effectiveHotel.currency === 'AED' || 
                  effectiveHotel.country === 'UAE' || 
                  effectiveHotel.city === 'Dubai' || 
                  String(effectiveHotel.country || '').trim().toUpperCase() === 'UAE' || 
                  String(effectiveHotel.city || '').trim().toLowerCase() === 'dubai' || 
                  currency === 'AED' || 
                  isUaeProperty(effectiveHotel);

    const [formData, setFormData] = useState(DEFAULT_TAX_STATE);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (open) {
            const initial = editingTax ? { ...editingTax } : { ...DEFAULT_TAX_STATE };
            if (editingTax) {
                if (!initial.serviceType) {
                    if (initial.applicableTo === 'F&B' || initial.applicableTo === 'Restaurant') {
                        initial.serviceType = 'f_and_b';
                    } else if (initial.applicableTo === 'Spa') {
                        initial.serviceType = 'spa_service';
                    } else if (initial.applicableTo === 'Room') {
                        initial.serviceType = 'room';
                    } else {
                        initial.serviceType = 'miscellaneous';
                    }
                }
                if (!initial.applicableTo) {
                    if (initial.serviceType === 'f_and_b') initial.applicableTo = 'F&B';
                    else if (initial.serviceType === 'spa_service') initial.applicableTo = 'Spa';
                    else if (initial.serviceType === 'room' || initial.serviceType === 'tourism_dirham' || initial.serviceType === 'municipality') initial.applicableTo = 'Room';
                    else initial.applicableTo = 'All';
                }
                if (!initial.status) initial.status = 'Active';
                initial.allow_itc = Boolean(initial.allow_itc || initial.allowItc);
                initial.is_exempt = Boolean(initial.is_exempt || initial.isExempt);
                const isUaeRecord = isUae || ['tourism_dirham', 'vat', 'municipality', 'service_charge'].includes(initial.serviceType) || initial.rateType === 'flat_per_night' || String(initial.name || '').toLowerCase().includes('tourism dirham');
                if (initial.is_exempt) {
                    initial.rate = '0'; initial.cgst = '0'; initial.sgst = '0';
                } else if (initial.rate !== undefined && initial.rate !== null && initial.rate !== '') {
                    initial.rate = initial.rate.toString();
                    if (!isUaeRecord) {
                        initial.cgst = initial.cgst !== undefined ? initial.cgst.toString() : (Number(initial.rate) / 2).toString();
                        initial.sgst = initial.sgst !== undefined ? initial.sgst.toString() : (Number(initial.rate) / 2).toString();
                    } else {
                        initial.cgst = '0';
                        initial.sgst = '0';
                    }
                } else if (initial.gstPct !== undefined) {
                    initial.rate = initial.gstPct.toString();
                    if (!isUaeRecord) {
                        initial.cgst = (initial.gstPct / 2).toString();
                        initial.sgst = (initial.gstPct / 2).toString();
                    } else {
                        initial.cgst = '0';
                        initial.sgst = '0';
                    }
                }
                const isFlatTourism = isUaeRecord && (initial.serviceType === 'tourism_dirham' || initial.rateType === 'flat_per_night' || String(initial.name || '').toLowerCase().includes('tourism dirham'));
                if (isFlatTourism) {
                    initial.rateType = 'flat_per_night';
                    initial.minThreshold = '';
                    initial.maxThreshold = '';
                } else {
                    initial.minThreshold = initial.minThreshold !== null && initial.minThreshold !== undefined ? initial.minThreshold : '';
                    initial.maxThreshold = initial.maxThreshold !== null && initial.maxThreshold !== undefined ? initial.maxThreshold : '';
                }
                if (initial.startDate) initial.startDate = initial.startDate.substring(0, 10);
                if (initial.endDate) initial.endDate = initial.endDate.substring(0, 10);
            }
            setFormData(initial);
            setErrors({});
        } else {
            setFormData({ ...DEFAULT_TAX_STATE });
            setErrors({});
        }
    }, [open, editingTax, isUae]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = type === 'checkbox' ? checked : value;

        setFormData(prev => {
            const updated = { ...prev, [name]: val };
            
            if (name === 'rate' && !updated.is_exempt) {
                const numRate = Number(val) || 0;
                if (!isUae) {
                    updated.cgst = (numRate / 2).toString();
                    updated.sgst = (numRate / 2).toString();
                } else {
                    updated.cgst = '0';
                    updated.sgst = '0';
                }
            }

            if (name === 'is_exempt') {
                if (val) {
                    updated.rate = '0';
                    updated.cgst = '0';
                    updated.sgst = '0';
                    updated.allow_itc = false;
                }
            }

            if (name === 'serviceType') {
                if (val === 'tourism_dirham') {
                    updated.rateType = 'flat_per_night';
                    updated.applicableTo = 'Room';
                    updated.minThreshold = '';
                    updated.maxThreshold = '';
                    const defaultRate = getTourismDirhamRate(effectiveHotel.rating, effectiveHotel.hotelType);
                    if (!editingTax && (!prev.rate || prev.rate === '0')) updated.rate = String(defaultRate);
                } else if (val === 'vat') {
                    updated.rateType = 'percentage';
                    updated.applicableTo = 'All';
                    if (!editingTax && (!prev.rate || prev.rate === '0')) updated.rate = '5';
                } else if (val === 'municipality') {
                    updated.rateType = 'percentage';
                    updated.applicableTo = 'Room';
                    if (!editingTax && (!prev.rate || prev.rate === '0')) updated.rate = '7';
                } else if (val === 'service_charge') {
                    updated.rateType = 'percentage';
                    updated.applicableTo = 'All';
                    if (!editingTax && (!prev.rate || prev.rate === '0')) updated.rate = '10';
                } else if (val === 'f_and_b') updated.applicableTo = 'F&B';
                else if (val === 'spa_service') updated.applicableTo = 'Spa';
                else if (val === 'room') updated.applicableTo = 'Room';
                else updated.applicableTo = 'All';
            }
            if (name === 'applicableTo') {
                if (val === 'F&B' || val === 'Restaurant') updated.serviceType = 'f_and_b';
                else if (val === 'Spa') updated.serviceType = 'spa_service';
                else if (val === 'Room' && updated.serviceType !== 'tourism_dirham' && updated.serviceType !== 'municipality') updated.serviceType = 'room';
                else if (val === 'All' && updated.serviceType !== 'vat' && updated.serviceType !== 'service_charge') updated.serviceType = 'miscellaneous';
            }

            return updated;
        });

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const getUaeServiceTypeLabel = (serviceType, name) => {
        const s = String(serviceType || '').toLowerCase();
        const n = String(name || '').toLowerCase();
        if (s === 'vat' || n.includes('vat')) return 'UAE Federal VAT';
        if (s === 'municipality' || n.includes('municipality')) return 'Dubai Municipality Fee';
        if (s === 'service_charge' || n.includes('service charge')) return 'Service Charge';
        if (s === 'tourism_dirham' || n.includes('tourism dirham')) return 'Tourism Dirham';
        if (s === 'room') return 'Room Accommodation';
        if (s === 'f_and_b') return 'Food & Beverage';
        if (s === 'spa_service') return 'Spa Services';
        return name || serviceType || 'Room Accommodation';
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.is_exempt) {
            if (formData.rate === '' || formData.rate === null || formData.rate === undefined) newErrors.rate = 'Rate is required';
            else if (isNaN(formData.rate) || Number(formData.rate) < 0) newErrors.rate = 'Must be a valid positive number';
        }
        if (!formData.serviceType && !formData.applicableTo) newErrors.serviceType = 'Service Type is required';

        const isFlatTourism = isUae && (formData.serviceType === 'tourism_dirham' || formData.rateType === 'flat_per_night');
        if (!isFlatTourism && !editingTax && formData.minThreshold !== '' && formData.maxThreshold !== '' && formData.maxThreshold !== null && formData.maxThreshold !== undefined) {
            if (Number(formData.minThreshold) > Number(formData.maxThreshold)) {
                newErrors.maxThreshold = 'Max threshold must be greater than Min threshold';
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = () => {
        if (validateForm()) {
            // When editing a slab for UAE hotels, simply send:
            // { "rate": 7.00 }
            // (Or for flat fee rates like Tourism Dirham: { "rate": 15.00, "rateType": "flat_per_night" }).
            // You do not need to send cgstPct or sgstPct.
            const isUaeTax = isUae || 
                ['tourism_dirham', 'vat', 'municipality', 'service_charge'].includes(editingTax?.serviceType) || 
                ['tourism_dirham', 'vat', 'municipality', 'service_charge'].includes(formData?.serviceType) ||
                editingTax?.rateType === 'flat_per_night' || 
                formData?.rateType === 'flat_per_night' ||
                String(editingTax?.name || '').toLowerCase().includes('tourism dirham') ||
                String(editingTax?.name || '').toLowerCase().includes('municipality') ||
                String(editingTax?.name || '').toLowerCase().includes('uae vat') ||
                String(editingTax?.name || '').toLowerCase().includes('service charge');

            const isFlatTourismFee = formData.serviceType === 'tourism_dirham' 
                || formData.rateType === 'flat_per_night'
                || editingTax?.serviceType === 'tourism_dirham'
                || editingTax?.rateType === 'flat_per_night'
                || (editingTax?.name && editingTax.name.toLowerCase().includes('tourism dirham'));

            if (isUaeTax && editingTax) {
                const patchPayload = {
                    rate: Number(formData.rate) || 0
                };
                if (isFlatTourismFee) {
                    patchPayload.rateType = 'flat_per_night';
                }
                onSave(patchPayload);
                return;
            }

            const isFlatTourism = isUaeTax && isFlatTourismFee;
            const rateVal = Number(formData.rate) || 0;
            const payload = {
                name: formData.name || undefined,
                rate: rateVal,
                rateType: isFlatTourism ? 'flat_per_night' : (formData.rateType || 'percentage'),
                gstPct: isFlatTourism ? 0 : (formData.is_exempt ? 0 : rateVal),
                cgstPct: (isUaeTax || isFlatTourism) ? 0 : (formData.is_exempt ? 0 : Number(formData.cgst || 0)),
                sgstPct: (isUaeTax || isFlatTourism) ? 0 : (formData.is_exempt ? 0 : Number(formData.sgst || 0)),
                serviceType: formData.serviceType || 'room',
                applicableTo: formData.applicableTo || 'Room',
                minThreshold: isFlatTourism ? null : (formData.minThreshold !== '' && formData.minThreshold !== null && formData.minThreshold !== undefined ? Number(formData.minThreshold) : null),
                maxThreshold: isFlatTourism ? null : (formData.maxThreshold !== '' && formData.maxThreshold !== null && formData.maxThreshold !== undefined ? Number(formData.maxThreshold) : null),
                allowItc: Boolean(formData.allow_itc),
                isExempt: Boolean(formData.is_exempt),
                status: formData.status || 'Active',
                startDate: formData.startDate || null,
                endDate: formData.endDate || null
            };
            onSave(payload);
        }
    };

    const isUaeTax = isUae || 
        ['tourism_dirham', 'vat', 'municipality', 'service_charge'].includes(editingTax?.serviceType) || 
        ['tourism_dirham', 'vat', 'municipality', 'service_charge'].includes(formData?.serviceType) ||
        editingTax?.rateType === 'flat_per_night' || 
        formData?.rateType === 'flat_per_night' ||
        String(editingTax?.name || '').toLowerCase().includes('tourism dirham') ||
        String(editingTax?.name || '').toLowerCase().includes('municipality') ||
        String(editingTax?.name || '').toLowerCase().includes('uae vat') ||
        String(editingTax?.name || '').toLowerCase().includes('service charge');

    const isFlatTourism = isUaeTax && (
        formData.serviceType === 'tourism_dirham' || 
        formData.rateType === 'flat_per_night' ||
        editingTax?.serviceType === 'tourism_dirham' ||
        editingTax?.rateType === 'flat_per_night' ||
        String(formData.name || editingTax?.name || '').toLowerCase().includes('tourism dirham')
    );

    return (
        <Modal open={open} onClose={loading ? null : onClose} disableEscapeKeyDown>
            <Box sx={{
                position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: { xs: '95%', sm: 580 }, maxHeight: '90vh', overflowY: 'auto',
                bgcolor: 'background.paper', borderRadius: 3, p: 4, boxShadow: 24,
                display: 'flex', flexDirection: 'column', gap: 2.5
            }}>
                <IconButton 
                    sx={{ position: 'absolute', top: 12, right: 12 }} 
                    onClick={onClose}
                    disabled={loading}
                >
                    <CloseIcon />
                </IconButton>

                <Box>
                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                        {isUaeTax 
                            ? (editingTax ? 'Edit Tax / Fee Rule' : 'Create Tax / Fee Rule')
                            : (editingTax ? 'Edit GST Tax Slab / Rule' : 'Create GST Tax Slab / Rule')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {isUaeTax
                            ? (editingTax 
                                ? (isFlatTourism ? 'Update statutory flat fee per bedroom per night.' : 'Update statutory percentage rate.')
                                : 'Configure statutory VAT, municipality fees, service charges, or Tourism Dirham.')
                            : 'Configure statutory GST slabs, thresholds, and ITC eligibility.'}
                    </Typography>
                </Box>

                {isUaeTax && editingTax ? (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="Service Type"
                                value={getUaeServiceTypeLabel(formData.serviceType, formData.name || editingTax.name)}
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label={isFlatTourism ? 'Fee per Bedroom / Night (AED)' : 'Percentage (%)'}
                                name="rate"
                                type="number"
                                placeholder={isFlatTourism ? 'e.g. 15' : 'e.g. 5'}
                                value={formData.rate}
                                onChange={handleChange}
                                error={!!errors.rate}
                                helperText={errors.rate || (isFlatTourism ? 'Statutory flat fee per bedroom per night' : 'Statutory percentage rate')}
                                disabled={loading}
                                autoFocus
                            />
                        </Grid>
                    </Grid>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={12}>
                            <TextField
                                select
                                fullWidth
                                label="Service Type"
                                name="serviceType"
                                value={formData.serviceType}
                                onChange={handleChange}
                                disabled={loading || !!editingTax}
                                error={!!errors.serviceType}
                                helperText={errors.serviceType}
                            >
                                {isUae ? [
                                    <MenuItem key="vat" value="vat">UAE Federal VAT</MenuItem>,
                                    <MenuItem key="municipality" value="municipality">Dubai Municipality Fee</MenuItem>,
                                    <MenuItem key="service_charge" value="service_charge">Service Charge</MenuItem>,
                                    <MenuItem key="tourism_dirham" value="tourism_dirham">Tourism Dirham</MenuItem>,
                                    <MenuItem key="room" value="room">Room Accommodation</MenuItem>,
                                    <MenuItem key="f_and_b" value="f_and_b">Food & Beverage</MenuItem>,
                                    <MenuItem key="spa_service" value="spa_service">Spa Services</MenuItem>,
                                    <MenuItem key="miscellaneous" value="miscellaneous">Miscellaneous Amenities</MenuItem>,
                                    <MenuItem key="cancellation_penalty" value="cancellation_penalty">Cancellation & No-Show Penalties</MenuItem>
                                ] : [
                                    <MenuItem key="room" value="room">Room Accommodation</MenuItem>,
                                    <MenuItem key="f_and_b" value="f_and_b">Food & Beverage (Standard)</MenuItem>,
                                    <MenuItem key="f_and_b_specified" value="f_and_b_specified">Food & Beverage (Specified Premises)</MenuItem>,
                                    <MenuItem key="liquor_vat" value="liquor_vat">Liquor VAT / Alcohol</MenuItem>,
                                    <MenuItem key="spa_service" value="spa_service">Spa Services</MenuItem>,
                                    <MenuItem key="miscellaneous" value="miscellaneous">Miscellaneous Amenities</MenuItem>,
                                    <MenuItem key="cancellation_penalty" value="cancellation_penalty">Cancellation & No-Show Penalties</MenuItem>
                                ]}
                            </TextField>
                        </Grid>

                        {isFlatTourism ? (
                            <Grid item xs={12}>
                                <Alert severity="info" sx={{ py: 0.5, borderRadius: 2 }}>
                                    <strong>Tourism Dirham Flat Fee:</strong> Charged per bedroom per night (statutory {getTourismDirhamTierLabel(effectiveHotel.rating, effectiveHotel.hotelType)}: AED {getTourismDirhamRate(effectiveHotel.rating, effectiveHotel.hotelType).toFixed(2)} benchmark).
                                </Alert>
                            </Grid>
                        ) : (
                            <>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label={`Min Threshold (${currencySymbol})`}
                                        name="minThreshold"
                                        type="number"
                                        placeholder="0"
                                        value={formData.minThreshold}
                                        onChange={handleChange}
                                        disabled={loading}
                                        InputLabelProps={{ shrink: Boolean(formData.minThreshold !== '' && formData.minThreshold !== undefined) }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label={`Max Threshold (${currencySymbol})`}
                                        name="maxThreshold"
                                        type="number"
                                        placeholder="Above / Unlimited"
                                        value={formData.maxThreshold}
                                        error={!!errors.maxThreshold}
                                        helperText={errors.maxThreshold || 'Leave blank for no upper limit'}
                                        disabled={loading}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Start Date"
                                name="startDate"
                                type="date"
                                value={formData.startDate || ''}
                                onChange={handleChange}
                                disabled={loading}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="End Date"
                                name="endDate"
                                type="date"
                                value={formData.endDate || ''}
                                onChange={handleChange}
                                disabled={loading}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                    {isFlatTourism 
                                        ? 'Statutory Tourism Dirham Fee' 
                                        : (isUae ? 'Statutory Rate / Fee Breakdown' : 'Statutory GST Breakdown')}
                                </Typography>
                            </Divider>
                        </Grid>

                        {(() => {
                            const isUaeLevyOrProperty = isUae || ['vat', 'municipality', 'service_charge', 'tourism_dirham'].includes(formData.serviceType) || formData.rateType === 'flat_per_night';
                            return (
                                <>
                                    <Grid item xs={12} sm={isUaeLevyOrProperty ? 12 : 4}>
                                        <TextField
                                            fullWidth
                                            label={
                                                isFlatTourism
                                                    ? 'Fee per Bedroom / Night (AED)'
                                                    : isUae
                                                        ? 'Percentage (%)'
                                                        : 'Total Rate (%)'
                                            }
                                            name="rate"
                                            type="number"
                                            placeholder="e.g. 18"
                                            value={formData.rate}
                                            onChange={handleChange}
                                            error={!!errors.rate}
                                            helperText={errors.rate || (isFlatTourism ? 'Statutory flat fee per bedroom per night' : '')}
                                            disabled={loading || formData.is_exempt}
                                            InputLabelProps={{ shrink: Boolean(formData.rate !== '' && formData.rate !== undefined) }}
                                        />
                                    </Grid>
                                    {!isUaeLevyOrProperty && (
                                        <>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    fullWidth
                                                    label="CGST (%)"
                                                    name="cgst"
                                                    type="number"
                                                    placeholder="Auto (50%)"
                                                    value={formData.cgst}
                                                    disabled={true}
                                                    helperText="Auto-split (50% of Total Rate)"
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    fullWidth
                                                    label="SGST (%)"
                                                    name="sgst"
                                                    type="number"
                                                    placeholder="Auto (50%)"
                                                    value={formData.sgst}
                                                    disabled={true}
                                                    helperText="Auto-split (50% of Total Rate)"
                                                    InputLabelProps={{ shrink: true }}
                                                />
                                            </Grid>
                                        </>
                                    )}
                                </>
                            );
                        })()}

                        <Grid item xs={12}>
                            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: 'rgba(0,0,0,0.01)', display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.is_exempt}
                                            onChange={(e) => handleChange({ target: { name: 'is_exempt', type: 'checkbox', checked: e.target.checked } })}
                                            color="warning"
                                            disabled={loading || isFlatTourism}
                                        />
                                    }
                                    label={isUae ? "Exempt (0%)" : "Exempt (0% GST)"}
                                />
                                {!isFlatTourism && (
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={formData.allow_itc}
                                                onChange={(e) => handleChange({ target: { name: 'allow_itc', type: 'checkbox', checked: e.target.checked } })}
                                                color="info"
                                                disabled={loading || formData.is_exempt}
                                            />
                                        }
                                        label="Allow ITC (Input Tax Credit)"
                                    />
                                )}
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.status === 'Active'}
                                            onChange={(e) => handleChange({ target: { name: 'status', type: 'checkbox', checked: e.target.checked ? 'Active' : 'Inactive' } })}
                                            color="success"
                                            disabled={loading}
                                        />
                                    }
                                    label={formData.status === 'Active' ? 'Active Rule' : 'Inactive Rule'}
                                />
                            </Paper>
                        </Grid>
                    </Grid>
                )}

                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                    <Button 
                        fullWidth 
                        variant="outlined" 
                        color="inherit" 
                        onClick={onClose} 
                        size="large"
                        disabled={loading}
                    >
                        Cancel
                    </Button>
                    <Button 
                        fullWidth 
                        variant="contained" 
                        onClick={handleSubmit} 
                        size="large"
                        disabled={loading}
                        startIcon={loading && <CircularProgress size={20} color="inherit" />}
                    >
                        {loading ? 'Saving...' : editingTax ? 'Update Rule' : 'Create Rule'}
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
